import { useState } from 'react';
import {
  MdArchive,
  MdArrowBack,
  MdBatteryFull,
  MdDarkMode,
  MdDeleteOutline,
  MdLightMode,
  MdMarkEmailUnread,
  MdMoreVert,
  MdOpenInNew,
  MdReply,
  MdShortcut,
  MdSignalCellularAlt,
  MdStarBorder,
  MdWifi,
} from 'react-icons/md';

import { cn } from '../lib/cn';
import { apercu, VARIABLES, type Sender } from '../lib/message-variables';
import { LogoCabinet } from './logo-cabinet';

/**
 * Les deux apparences de Gmail.
 *
 * Le thème sombre n'est pas une coquetterie : une part des patients lisent
 * leurs courriels dessus, et c'est là qu'un encart de signature clair se met
 * à trancher au milieu d'une page noire. Autant le voir avant d'envoyer.
 *
 * Teintes relevées sur l'application elle-même, y compris le bleu des liens,
 * qui s'éclaircit en sombre faute de contraste suffisant autrement.
 */
export const THEMES = {
  clair: {
    fond: '#ffffff',
    texte: '#202124',
    discret: '#5f6368',
    filet: '#e8eaed',
    lien: '#1a73e8',
    vide: '#9aa0a6',
    etat: '#000000',
    // Les deux couleurs du logo du cabinet, reprises de la marque : bleu nuit
    // et or. Éclaircies en thème sombre, où le bleu nuit vire au trou noir et
    // l'or sourd perd tout éclat.
    marque: '#0a385b',
    accent: '#a67b3d',
  },
  sombre: {
    fond: '#1f1f1f',
    texte: '#e8eaed',
    discret: '#9aa0a6',
    filet: '#3c4043',
    lien: '#8ab4f8',
    vide: '#5f6368',
    etat: '#ffffff',
    marque: '#b0c7db',
    accent: '#cbab7b',
  },
} as const;

type Theme = (typeof THEMES)[keyof typeof THEMES];

export type Bouton = {
  label: string;
  /** Adresse réelle : cliquable dans l'aperçu, pour aller voir la page. */
  url?: string;
};

/** Le bouton de fin de message, tel que le courriel le porte. */
function BoutonMail({ bouton, theme }: { bouton: Bouton; theme: Theme }) {
  const classes =
    'mt-4 inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-[13px] font-semibold no-underline';
  const style = { backgroundColor: theme.marque, color: theme.fond };
  const contenu = (
    <>
      {bouton.label}
      <MdOpenInNew aria-hidden="true" className="size-3.5" />
    </>
  );

  return bouton.url && /^https:\/\//.test(bouton.url) ? (
    <a href={bouton.url} target="_blank" rel="noreferrer" style={style} className={classes}>
      {contenu}
    </a>
  ) : (
    <span style={style} className={classes}>
      {contenu}
    </span>
  );
}

/**
 * Le message tel que le patient le recevra, dans un vrai téléphone.
 *
 * Pas un ornement. Le corps est truffé d'accolades, et personne ne peut juger
 * d'une phrase en lisant « Bonjour {{prenom}} » : c'est ici qu'on voit si
 * elle tombe juste. Le cadre du téléphone n'est pas décoratif non plus — un
 * message lu sur 300 px de large ne se coupe pas aux mêmes endroits que dans
 * une zone de texte pleine largeur, et c'est sur un téléphone que le patient
 * le lira.
 */
export function ApercuMessage({
  expediteur,
  cabinet,
  logoUrl,
  objet,
  corps,
  signature,
  logoSignature,
  bouton,
  titre = 'Aperçu email',
  entete = true,
}: {
  expediteur: Sender;
  /** Nom du cabinet : ce que `{{cabinet}}` et le logo affichent. */
  cabinet: string;
  /** Logo déposé par le cabinet, affiché en tête de signature s'il existe. */
  logoUrl?: string | null;
  objet: string;
  corps: string;
  signature: string;
  logoSignature: boolean;
  /** Bouton de fin de message (le lien d'avis Google), s'il y en a un. */
  bouton?: Bouton;
  titre?: string;
  /**
   * Le titre et le sélecteur de thème au-dessus du téléphone.
   *
   * Retirés là où l'aperçu n'est pas l'objet de l'écran mais sa preuve — la
   * boîte de mise en route des avis, par exemple : on y montre un message,
   * pas un banc d'essai, et une bascule clair/sombre y proposait un réglage
   * dont personne n'avait besoin à ce moment-là.
   */
  entete?: boolean;
}) {
  const [sombre, setSombre] = useState(false);
  const theme = sombre ? THEMES.sombre : THEMES.clair;

  return (
    // Même largeur que le téléphone, et centré comme lui : la ligne de titre
    // se cale exactement sur ses bords plutôt que de flotter au-dessus.
    <div className="mx-auto flex w-full max-w-[300px] flex-col gap-3">
      {entete ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-content">{titre}</p>
          <ChoixTheme sombre={sombre} onChange={setSombre} />
        </div>
      ) : null}

      <Telephone theme={theme}>
        <Gmail
          theme={theme}
          expediteur={expediteur}
          cabinet={cabinet}
          logoUrl={logoUrl}
          sujet={apercu(objet, cabinet)}
          // Bruts : c'est `TexteMail` qui remplace les variables, pour pouvoir
          // les distinguer du reste du message.
          corps={corps}
          signature={signature.trim()}
          logoSignature={logoSignature}
          bouton={bouton}
        />
      </Telephone>
    </div>
  );
}

/**
 * Clair ou sombre, en deux pastilles.
 *
 * Deux boutons plutôt qu'un interrupteur : un interrupteur demande de savoir
 * ce qu'il allume — « Mode sombre » coché, c'est sombre ou c'est clair ? Ici
 * l'icône allumée est l'état courant, sans rien à interpréter.
 */
function ChoixTheme({
  sombre,
  onChange,
}: {
  sombre: boolean;
  onChange: (sombre: boolean) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Apparence de l’aperçu"
      className="flex items-center gap-0.5 rounded-full border border-border-subtle bg-surface-muted p-0.5"
    >
      <Pastille
        actif={!sombre}
        label="Aperçu en clair"
        onClick={() => onChange(false)}
        icone={<MdLightMode className="size-4" />}
      />
      <Pastille
        actif={sombre}
        label="Aperçu en sombre"
        onClick={() => onChange(true)}
        icone={<MdDarkMode className="size-4" />}
      />
    </div>
  );
}

function Pastille({
  actif,
  label,
  icone,
  onClick,
}: {
  actif: boolean;
  label: string;
  icone: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={actif}
      className={cn(
        'grid size-7 place-items-center rounded-full transition-colors',
        actif
          ? 'bg-surface text-content shadow-sm'
          : 'text-content-subtle hover:text-content-muted',
      )}
    >
      {icone}
    </button>
  );
}

/**
 * Carcasse d'iPhone : coque sombre, écran arrondi, îlot dynamique.
 *
 * Tout est en CSS plutôt qu'en image : le cadre doit suivre la largeur de la
 * colonne, et une capture d'écran aurait vieilli au premier changement de
 * maquette.
 */
export function Telephone({ theme, children }: { theme: Theme; children: React.ReactNode }) {
  return (
    <div className="relative w-full max-w-[300px]">
      {/* Boutons latéraux, en relief sur la tranche de la coque. */}
      <span
        aria-hidden="true"
        className="absolute -left-[2px] top-[104px] h-12 w-[2px] rounded-l-sm bg-[#2a2a2e]"
      />
      <span
        aria-hidden="true"
        className="absolute -left-[2px] top-[164px] h-12 w-[2px] rounded-l-sm bg-[#2a2a2e]"
      />
      <span
        aria-hidden="true"
        className="absolute -right-[2px] top-[132px] h-16 w-[2px] rounded-r-sm bg-[#2a2a2e]"
      />

      {/* Coque fine : 6 px de tranche, et le rayon intérieur vaut celui de la
          coque moins cette épaisseur — sans quoi l'écran et le châssis ne
          seraient plus concentriques et le cadre paraîtrait épais d'un côté. */}
      <div className="rounded-[2.6rem] bg-[#1c1c1e] p-[6px] shadow-[0_24px_60px_-24px_rgba(6,34,54,0.55)]">
        <div
          style={{ backgroundColor: theme.fond }}
          className="relative flex h-[580px] flex-col overflow-hidden rounded-[2.225rem]"
        >
          {/* Îlot dynamique : posé par-dessus la barre d'état, comme sur
              l'appareil — le contenu passe dessous, jamais derrière. */}
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-2.5 z-20 h-[26px] w-[90px] -translate-x-1/2 rounded-full bg-black"
          />

          <BarreEtat theme={theme} />
          {children}
        </div>
      </div>
    </div>
  );
}

/** 9 h 41 : l'heure des maquettes Apple depuis la première keynote. */
function BarreEtat({ theme }: { theme: Theme }) {
  return (
    <div
      style={{ color: theme.etat }}
      className="flex shrink-0 items-center justify-between px-6 pb-1 pt-[14px] text-[11px] font-semibold"
    >
      <span className="tabular-nums">9:41</span>
      <span aria-hidden="true" className="flex items-center gap-[3px]">
        <MdSignalCellularAlt className="size-[13px]" />
        <MdWifi className="size-[13px]" />
        <MdBatteryFull className="size-[15px] rotate-90" />
      </span>
    </div>
  );
}

/**
 * Ce qu'un client de messagerie transforme en lien : adresses web, adresses
 * électroniques, numéros de téléphone français.
 *
 * Reproduit ici parce que l'aperçu ment sans ça : le cabinet qui écrit son
 * numéro dans un message doit voir qu'il sera cliquable, et qu'il ressortira
 * en bleu au milieu de sa phrase.
 */
const LIENS =
  /(https?:\/\/[^\s]+|www\.[^\s]+|[\w.+-]+@[\w-]+\.[\w.-]+|(?:\+33\s?|0)\d(?:[\s.-]?\d{2}){4})/g;

/** Ponctuation qui suit une adresse sans en faire partie. */
const PONCTUATION_FINALE = /[.,;:!?)\]]+$/;

type Fragment = { texte: string; lien: boolean };

function decouperLiens(texte: string): Fragment[] {
  const fragments: Fragment[] = [];
  let curseur = 0;

  for (const trouve of texte.matchAll(LIENS)) {
    const debut = trouve.index ?? 0;
    if (debut > curseur) fragments.push({ texte: texte.slice(curseur, debut), lien: false });

    // « Rendez-vous sur fidalia.fr. » : le point final appartient à la phrase,
    // pas à l'adresse — le souligner ferait un lien qui déborde.
    const brut = trouve[0];
    const queue = PONCTUATION_FINALE.exec(brut)?.[0] ?? '';
    const lien = queue ? brut.slice(0, -queue.length) : brut;

    fragments.push({ texte: lien, lien: true });
    if (queue) fragments.push({ texte: queue, lien: false });

    curseur = debut + brut.length;
  }

  if (curseur < texte.length) fragments.push({ texte: texte.slice(curseur), lien: false });
  return fragments;
}

/**
 * Découpe le texte en variables remplacées et en texte ordinaire.
 *
 * Le remplacement se fait ici plutôt que par `apercu()` en amont parce que
 * l'aperçu doit *montrer* d'où vient chaque mot : une fois « {{prenom}} »
 * devenu « Camille », plus rien ne distinguait ce que Fidalia a rempli de ce
 * que le cabinet a écrit.
 */
function decouperVariables(texte: string, cabinet: string): { texte: string; variable: boolean }[] {
  const fragments: { texte: string; variable: boolean }[] = [];
  let curseur = 0;

  for (const trouve of texte.matchAll(/\{\{(\w+)\}\}/g)) {
    const debut = trouve.index ?? 0;
    if (debut > curseur) fragments.push({ texte: texte.slice(curseur, debut), variable: false });

    // Variable inconnue : laissée telle quelle, accolades comprises. C'est
    // exactement ce que le patient recevrait, et la mettre en gras aurait
    // habillé une faute de frappe en intention.
    // `{{cabinet}}` prend le nom du cabinet connecté, pas un exemple : c'est
    // la seule variable dont la vraie valeur est déjà connue, et l'aperçu doit
    // montrer ce que le patient lira.
    const variable = VARIABLES.find((candidate) => candidate.cle === trouve[1]);
    const valeur = trouve[1] === 'cabinet' && cabinet ? cabinet : (variable?.exemple ?? trouve[0]);
    fragments.push({ texte: valeur, variable: variable !== undefined });

    curseur = debut + trouve[0].length;
  }

  if (curseur < texte.length) fragments.push({ texte: texte.slice(curseur), variable: false });
  return fragments;
}

/**
 * Texte d'un courriel : ses variables en gras, ses adresses en lien.
 *
 * Des `span` et non des `a` : c'est une maquette, un clic ne doit mener nulle
 * part — mais il doit *avoir l'air* de mener quelque part.
 */
function TexteMail({ contenu, lien, cabinet }: { contenu: string; lien: string; cabinet: string }) {
  return (
    <>
      {decouperVariables(contenu, cabinet).map((fragment, index) =>
        fragment.variable ? (
          <strong key={index} className="font-semibold">
            {fragment.texte}
          </strong>
        ) : (
          // Les adresses ne sont cherchées que hors des variables : aucune
          // n'en contient, et les y chercher aurait doublé le travail.
          decouperLiens(fragment.texte).map((morceau, rang) =>
            morceau.lien ? (
              <span
                key={`${index}-${rang}`}
                style={{ color: lien }}
                className="underline underline-offset-2"
              >
                {morceau.texte}
              </span>
            ) : (
              <span key={`${index}-${rang}`}>{morceau.texte}</span>
            ),
          )
        ),
      )}
    </>
  );
}

/**
 * Un courriel ouvert dans Gmail : barre d'actions, objet, expéditeur, corps.
 *
 * Reproduit d'assez près pour qu'on s'y reconnaisse, sans chercher le
 * trompe-l'œil — ce qu'on vient vérifier ici, c'est le texte, pas la fidélité
 * de l'imitation.
 */
function Gmail({
  theme,
  expediteur,
  cabinet,
  logoUrl,
  sujet,
  corps,
  signature,
  logoSignature,
  bouton,
}: {
  theme: Theme;
  expediteur: Sender;
  cabinet: string;
  logoUrl?: string | null;
  sujet: string;
  corps: string;
  signature: string;
  logoSignature: boolean;
  bouton?: Bouton;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        style={{ color: theme.discret }}
        className="flex shrink-0 items-center gap-4 px-4 pb-2 pt-3"
      >
        <MdArrowBack aria-hidden="true" className="size-[18px]" />
        <span className="ml-auto flex items-center gap-4">
          <MdArchive aria-hidden="true" className="size-[18px]" />
          <MdDeleteOutline aria-hidden="true" className="size-[18px]" />
          <MdMarkEmailUnread aria-hidden="true" className="size-[18px]" />
          <MdMoreVert aria-hidden="true" className="size-[18px]" />
        </span>
      </div>

      {/* Le corps défile, pas le téléphone : un message long doit se lire
          comme sur l'appareil, en faisant glisser la page. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        <h3 style={{ color: theme.texte }} className="pr-2 text-[17px] leading-snug">
          {sujet || 'Sans objet'}
        </h3>

        <div className="mt-3 flex items-center gap-2.5">
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-[#1a73e8] text-[13px] font-medium text-white">
            {expediteur.nom.charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <p style={{ color: theme.texte }} className="truncate text-[13px] font-medium">
              {expediteur.nom}
            </p>
            {/* L'adresse plutôt que « à moi ▾ » : c'est elle qu'on vient
                vérifier, et le destinataire n'apprend rien ici. */}
            <p style={{ color: theme.discret }} className="truncate text-[11px]">
              {expediteur.email}
            </p>
          </div>
          <span style={{ color: theme.discret }} className="shrink-0 text-[11px]">
            09:12
          </span>
          <MdStarBorder
            aria-hidden="true"
            style={{ color: theme.discret }}
            className="size-[17px] shrink-0"
          />
        </div>

        <p
          style={{ color: theme.texte }}
          className="mt-4 whitespace-pre-wrap text-[13.5px] leading-[1.6]"
        >
          {corps ? (
            <TexteMail contenu={corps} lien={theme.lien} cabinet={cabinet} />
          ) : (
            <span style={{ color: theme.vide }}>Votre message apparaîtra ici.</span>
          )}
        </p>

        {/* Le bouton de fin de message : comme dans le courriel envoyé, il porte
            le seul geste attendu du patient, sous le texte et avant la
            signature. */}
        {bouton ? <BoutonMail bouton={bouton} theme={theme} /> : null}

        {/* Un cran en dessous du corps : un peu plus petite, en gris
            secondaire. C'est ainsi que les clients de messagerie traitent les
            signatures, et ce décrochage suffit à dire « ceci n'est plus le
            message ». Pas d'italique : on y met des adresses et des numéros,
            qui s'y lisent mal. */}
        {/* Un filet et un décrochage de taille : la signature n'est plus le
            message, et elle doit se lire comme telle sans pour autant se
            déguiser en encart. */}
        {signature ? (
          <p
            style={{ color: theme.discret, borderColor: theme.filet }}
            className="mt-5 whitespace-pre-wrap border-t pt-3 text-[11.5px] leading-[1.5]"
          >
            {/* Le logo ouvre la signature, sur sa propre ligne : à côté du
                texte il l'aurait réduite à une colonne étroite sur un écran de
                téléphone. */}
            {logoSignature ? (
              <LogoCabinet
                nom={cabinet}
                url={logoUrl}
                marque={theme.marque}
                accent={theme.accent}
              />
            ) : null}
            <TexteMail contenu={signature} lien={theme.lien} cabinet={cabinet} />
          </p>
        ) : null}

        {/* La même mention qu'au bas des messages réellement envoyés
            (`message-renderer.ts`) : l'aperçu doit montrer ce que le patient
            recevra, mention de pied comprise. Un cran plus pâle que la
            signature, qui est déjà plus pâle que le message. */}
        <p style={{ color: theme.vide }} className="mt-4 text-[10.5px] leading-[1.4]">
          Envoyé automatiquement via fidalia.fr
        </p>
      </div>

      {/* Barre de réponse, collée en bas comme dans l'application. */}
      <div
        style={{ borderColor: theme.filet, color: theme.discret }}
        className="flex shrink-0 items-center gap-2 border-t px-4 py-3"
      >
        <button
          type="button"
          tabIndex={-1}
          style={{ borderColor: theme.filet }}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-full border py-1.5 text-[12px]"
        >
          <MdReply aria-hidden="true" className="size-[15px]" />
          Répondre
        </button>
        <button
          type="button"
          tabIndex={-1}
          style={{ borderColor: theme.filet }}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-full border py-1.5 text-[12px]"
        >
          <MdShortcut aria-hidden="true" className="size-[15px]" />
          Transférer
        </button>
      </div>
    </div>
  );
}
